import { ReactNode } from "react";

import {
  Box,
  Container,
  Paper,
  PaperProps,
  Stack,
  Typography,
  paperClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { OrnamentLandscape2 } from "@/components/ornaments";

interface TakeawayProps extends PaperProps {
  action?: ReactNode;
  featured?: boolean;
  footnote?: string;
  overtitle?: string;
  text?: string;
  title?: string;
}

const PREFIX = "BaikiTakeaway";
const classes = {
  root: `${PREFIX}-Root`,
  overititle: `${PREFIX}-Overtitle`,
  text: `${PREFIX}-Text`,
  title: `${PREFIX}-Title`,
};

const Root = styled(Paper, {
  shouldForwardProp: (prop: string) => !["featured"].includes(prop),
})<TakeawayProps>(({ featured, theme }) => ({
  alignItems: "stretch",
  borderRadius: theme.shape.borderRadius,
  boxShadow: `${theme.shadows[2]}`,
  color: theme.palette.text.secondary,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  justifyContent: "stretch",
  minHeight: "33vh",
  overflow: "hidden",
  padding: theme.spacing(0.5),
  position: "relative",
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    borderRadius: (theme.shape.borderRadius as number) * 2,
    minHeight: "22vh",
  },
  [theme.breakpoints.up("md")]: {
    borderRadius: (theme.shape.borderRadius as number) * 3,
  },
  [`& > .${paperClasses.root}`]: {
    alignItems: "center",
    backgroundColor: theme.palette.background.default,
    borderRadius: (theme.shape.borderRadius as number) * 0.8,
    boxShadow: "none",
    display: "flex",
    flexGrow: 1,
    justifyContent: "center",
    overflow: "hidden",
    padding: theme.spacing(featured ? 4 : 3),
    position: "relative",
    [theme.breakpoints.up("sm")]: {
      borderRadius: (theme.shape.borderRadius as number) * 1.8,
    },
    [theme.breakpoints.up("md")]: {
      borderRadius: (theme.shape.borderRadius as number) * 2.8,
      padding: theme.spacing(featured ? 8 : 6),
    },
  },
}));

export const Takeaway: React.FC<TakeawayProps> = ({
  action,
  footnote,
  overtitle,
  text,
  title,
  ...props
}) => {
  const { featured } = props;
  return (
    <Root variant="outlined" className={classes.root} {...props}>
      <Paper variant="outlined">
        <OrnamentLandscape2
          sx={{
            bottom: 0,
            left: 0,
            opacity: 0.15,
            position: "absolute",
            right: 0,
            transform: "rotate(180deg) translateY(-50%)",
          }}
        />
        <Container
          disableGutters
          maxWidth="md"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Stack spacing={{ xs: 1, md: 2 }}>
            {overtitle && (
              <Typography
                color="text.secondary"
                variant="overline"
                style={{ textWrap: "balance" }}
              >
                {overtitle}
              </Typography>
            )}
            {title && (
              <Typography
                color="primary.dark"
                style={{ textWrap: "balance" }}
                variant={featured ? "h3" : "h4"}
              >
                {title.replace(/["]+/g, "")}
              </Typography>
            )}
            <Stack alignItems="center">
              {text && (
                <Typography
                  align="center"
                  color="text.secondary"
                  style={{ textWrap: "balance" }}
                  variant="subtitle1"
                >
                  {text}
                </Typography>
              )}
              {action && <Box sx={{ mt: 2 }}>{action}</Box>}
              {footnote ? (
                <Typography
                  color="textDisabled"
                  sx={{ textWrap: "balance", mt: 3 }}
                  variant="caption"
                >
                  {footnote}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Container>
      </Paper>
    </Root>
  );
};
